import React, { useCallback, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft, FileText, Flag, Wallet, Plus, Check, X as XIcon,
  Trash2, Loader2, AlertCircle, CheckCircle2, CircleDot, Clock, Sparkles,
  ExternalLink, ListChecks, MapPin, ChevronRight,
} from 'lucide-react'
import ImmigrationAssistantSheet from './components/ImmigrationAssistantSheet'
import { Card, CardContent } from '../../components/ui/card'
import { Skeleton } from '../../components/ui/Skeleton'
import PullToRefresh from '../../components/PullToRefresh'
import { performRefresh } from '../../lib/refresh'
import { useImmigrationCountries } from '../../hooks/useImmigration'
import {
  useImmigrationDocuments, useImmigrationMilestones, useImmigrationCosts,
} from '../../hooks/useImmigrationTracker'
import { useAuth } from '../../contexts/AuthContext'
import { hasOwnerAccess } from '../../lib/owner'
import { useConfirm } from '../../contexts/ConfirmDialogContext'
import { cn } from '../../lib/utils'

// Per-country tracker: docs + milestones + costs.
// Owner-gated like the parent /immigration page.

function fmtMoney(n) {
  if (n == null) return '—'
  return Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })
}

export function ImmigrationCountryPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { slug } = useParams()
  const { user } = useAuth()
  const { countries, loading: countriesLoading } = useImmigrationCountries()
  const docs = useImmigrationDocuments(slug)
  const milestones = useImmigrationMilestones(slug)
  const costs = useImmigrationCosts(slug)
  const [assistantOpen, setAssistantOpen] = useState(false)

  React.useEffect(() => {
    if (!user) return
    if (!hasOwnerAccess(user)) navigate('/', { replace: true })
  }, [user, navigate])

  const country = useMemo(
    () => countries.find((c) => c.slug === slug),
    [countries, slug],
  )

  const handleRefresh = useCallback(
    () => performRefresh([docs.refetch, milestones.refetch, costs.refetch]),
    [docs.refetch, milestones.refetch, costs.refetch],
  )

  if (countriesLoading) {
    return <div className="p-4"><Skeleton className="h-32 w-full" /></div>
  }

  if (!country) {
    return (
      <div className="p-4">
        <button
          type="button"
          onClick={() => navigate('/immigration')}
          className="text-sm text-app-primary-500 hover:underline"
        >
          ← {t('immigration.title')}
        </button>
        <p className="mt-4 text-sm text-muted-foreground" dir="auto">
          {t('immigration.notFound')}
        </p>
      </div>
    )
  }

  const isAr = i18n.language === 'ar'
  const name = isAr ? country.name_ar : country.name_en

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="space-y-4 content-fade pb-8">
        <div className="flex items-center gap-2 mb-2">
          <button
            type="button"
            onClick={() => navigate('/immigration')}
            aria-label={t('settings.back')}
            className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground"
          >
            <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-foreground flex items-center gap-2" dir="auto">
              <span className="text-2xl">{country.flag_emoji}</span>
              {name}
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5" dir="auto">
              {t('immigration.tracker.subtitle')}
            </p>
          </div>
        </div>

        {/* AI assistant — primary "I don't know what to do" affordance. */}
        <button
          type="button"
          onClick={() => setAssistantOpen(true)}
          className="w-full inline-flex items-center gap-3 px-4 py-3 rounded-xl bg-app-primary-500/10 hover:bg-app-primary-500/15 text-app-primary-500 transition-colors"
          dir="auto"
        >
          <Sparkles className="w-5 h-5 flex-shrink-0" />
          <div className="flex-1 text-start min-w-0">
            <p className="text-sm font-semibold">{t('immigration.assistant.askAboutCountry', { country: name })}</p>
            <p className="text-[11px] opacity-80">{t('immigration.assistant.subtitlePerCountry')}</p>
          </div>
        </button>

        <PathwaySection country={country} milestonesApi={milestones} />

        <DocumentsSection docs={docs} countrySlug={slug} />
        <MilestonesSection milestones={milestones} countrySlug={slug} />
        <CostsSection costs={costs} countrySlug={slug} />

        <ImmigrationAssistantSheet
          open={assistantOpen}
          countrySlug={slug}
          onClose={() => setAssistantOpen(false)}
        />
      </div>
    </PullToRefresh>
  )
}

// ---------- PATHWAY GUIDE ------------------------------------------------

function PathwaySection({ country, milestonesApi }) {
  const { t, i18n } = useTranslation()
  const isAr = i18n.language === 'ar'
  const steps = country?.pathway_steps || []
  if (steps.length === 0) return null

  return (
    <Card>
      <CardContent className="p-3 sm:p-4 space-y-3">
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-app-primary-500 flex-shrink-0" />
          <h2 className="text-sm font-semibold text-foreground flex-1" dir="auto">
            {t('immigration.pathway.title')}
            <span className="ms-1.5 text-muted-foreground font-normal">({steps.length})</span>
          </h2>
        </div>
        <p className="text-xs text-muted-foreground" dir="auto">
          {t('immigration.pathway.subtitle')}
        </p>
        <ol className="space-y-2.5">
          {steps.map((step) => (
            <PathwayStep
              key={step.step}
              step={step}
              isAr={isAr}
              countrySlug={country.slug}
              milestonesApi={milestonesApi}
            />
          ))}
        </ol>
      </CardContent>
    </Card>
  )
}

function PathwayStep({ step, isAr, countrySlug, milestonesApi }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [tracking, setTracking] = useState(false)
  const title = isAr ? step.title_ar : step.title_en
  const what = isAr ? step.what_ar : step.what_en
  const duration = isAr ? step.duration_ar : step.duration_en

  // Already a milestone? Match by title.
  const alreadyTracked = milestonesApi.items.some((m) => m.title === title)

  const trackStep = async () => {
    setTracking(true)
    await milestonesApi.create({
      country_slug: countrySlug,
      title,
      description: what,
      status: 'planned',
      display_order: step.step,
    })
    setTracking(false)
  }

  return (
    <li className="flex gap-3" dir="auto">
      <span className="flex-shrink-0 w-7 h-7 rounded-full bg-app-primary-500/15 text-app-primary-500 inline-flex items-center justify-center text-xs font-bold">
        {step.step}
      </span>
      <div className="flex-1 min-w-0 rounded-lg border border-input p-3 space-y-1.5">
        <p className="text-sm font-semibold text-foreground">{title}</p>
        <p className="text-xs text-muted-foreground leading-relaxed">{what}</p>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
          {duration && (
            <span className="inline-flex items-center gap-1 text-muted-foreground">
              <Clock className="w-3 h-3" />
              {duration}
            </span>
          )}
          {step.cost_sar_approx > 0 && (
            <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400 font-mono">
              ~{step.cost_sar_approx.toLocaleString('en-US')} SAR
            </span>
          )}
        </div>
        {(step.links || []).length > 0 && (
          <div className="flex flex-wrap gap-2 pt-1">
            {step.links.map((link, i) => (
              <a
                key={i}
                href={link.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-[11px] text-app-primary-500 hover:underline"
                dir="ltr"
              >
                <ExternalLink className="w-3 h-3" />
                {link.label}
              </a>
            ))}
          </div>
        )}
        <div className="flex flex-wrap gap-1 pt-1">
          <button
            type="button"
            onClick={trackStep}
            disabled={tracking || alreadyTracked}
            className={cn(
              'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium',
              alreadyTracked
                ? 'text-emerald-700 dark:text-emerald-400 bg-emerald-500/10 cursor-default'
                : 'text-app-primary-500 hover:bg-app-primary-500/10',
            )}
          >
            {tracking ? <Loader2 className="w-3 h-3 animate-spin" />
              : alreadyTracked ? <CheckCircle2 className="w-3 h-3" />
              : <ListChecks className="w-3 h-3" />}
            {alreadyTracked
              ? t('immigration.pathway.alreadyTracking')
              : t('immigration.pathway.trackThis')}
          </button>
          <button
            type="button"
            onClick={() => navigate(`/immigration/${countrySlug}/step/${step.step}`)}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium text-muted-foreground hover:bg-accent"
          >
            {t('immigration.pathway.viewDetails')}
            <ChevronRight className="w-3 h-3 rtl:-scale-x-100" />
          </button>
        </div>
      </div>
    </li>
  )
}

// ---------- DOCUMENTS ---------------------------------------------------

const DOC_STATUSES = ['not_started', 'in_progress', 'obtained', 'expired']
const DOC_CATEGORIES = ['identity', 'education', 'language', 'experience', 'financial', 'medical', 'general']

function DocumentsSection({ docs, countrySlug }) {
  const { t } = useTranslation()
  const [adding, setAdding] = useState(false)

  return (
    <Card>
      <CardContent className="p-3 sm:p-4 space-y-3">
        <SectionHeader
          icon={FileText}
          title={t('immigration.tracker.docs.title')}
          count={docs.items.length}
          onAdd={() => setAdding(true)}
        />
        {docs.loading ? (
          <Skeleton className="h-16 w-full" />
        ) : docs.items.length === 0 && !adding ? (
          <EmptyHint label={t('immigration.tracker.docs.empty')} />
        ) : (
          <div className="space-y-1.5">
            {docs.items.map((d) => (
              <DocumentRow key={d.id} doc={d} api={docs} />
            ))}
          </div>
        )}
        {adding && (
          <AddDocForm
            countrySlug={countrySlug}
            onSubmit={async (values) => {
              await docs.create(values)
              setAdding(false)
            }}
            onCancel={() => setAdding(false)}
          />
        )}
      </CardContent>
    </Card>
  )
}

function DocumentRow({ doc, api }) {
  const { t } = useTranslation()
  const confirm = useConfirm()
  const cycleStatus = async () => {
    const idx = DOC_STATUSES.indexOf(doc.status)
    const next = DOC_STATUSES[(idx + 1) % DOC_STATUSES.length]
    await api.update(doc.id, { status: next })
  }
  const onDelete = async () => {
    const ok = await confirm({
      title: t('common.confirmDelete'),
      message: doc.name,
      destructive: true,
    })
    if (ok) await api.remove(doc.id)
  }
  return (
    <div className="flex items-center gap-2 px-2.5 py-2 rounded-md border border-input">
      <button
        type="button"
        onClick={cycleStatus}
        className={cn(
          'flex-shrink-0 w-7 h-7 rounded-full inline-flex items-center justify-center text-[10px] font-semibold',
          doc.status === 'obtained' && 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
          doc.status === 'in_progress' && 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
          doc.status === 'not_started' && 'bg-muted text-muted-foreground',
          doc.status === 'expired' && 'bg-red-500/15 text-red-700 dark:text-red-400',
        )}
        title={t(`immigration.tracker.docStatus.${doc.status}`)}
      >
        {doc.status === 'obtained' ? <CheckCircle2 className="w-4 h-4" />
          : doc.status === 'in_progress' ? <CircleDot className="w-4 h-4" />
          : doc.status === 'expired' ? <AlertCircle className="w-4 h-4" />
          : '—'}
      </button>
      <div className="flex-1 min-w-0" dir="auto">
        <p className="text-sm font-medium text-foreground truncate">{doc.name}</p>
        <p className="text-[10px] text-muted-foreground truncate">
          {t(`immigration.tracker.docCategory.${doc.category}`)}
          {doc.expires_at ? ` · ${t('immigration.tracker.docs.expires')}: ${doc.expires_at}` : ''}
        </p>
      </div>
      <button
        type="button"
        onClick={onDelete}
        aria-label={t('common.delete')}
        className="p-1.5 rounded-md text-muted-foreground hover:bg-accent hover:text-red-600"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  )
}

function AddDocForm({ countrySlug, onSubmit, onCancel }) {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [category, setCategory] = useState('general')
  const [shared, setShared] = useState(false)
  const [saving, setSaving] = useState(false)
  const submit = async (e) => {
    e.preventDefault()
    if (!name.trim()) return
    setSaving(true)
    await onSubmit({
      name: name.trim(),
      category,
      country_slug: shared ? null : countrySlug,
    })
    setSaving(false)
  }
  return (
    <form onSubmit={submit} className="space-y-2 p-3 rounded-md border border-input bg-muted/30">
      <input
        autoFocus
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={t('immigration.tracker.docs.namePlaceholder')}
        className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
      />
      <div className="flex flex-wrap gap-1.5">
        {DOC_CATEGORIES.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setCategory(c)}
            className={cn(
              'px-2 py-1 rounded-full text-[11px] border',
              category === c
                ? 'bg-app-primary-500 text-white border-app-primary-500'
                : 'border-input text-muted-foreground hover:bg-accent',
            )}
          >
            {t(`immigration.tracker.docCategory.${c}`)}
          </button>
        ))}
      </div>
      <label className="inline-flex items-center gap-2 text-xs text-muted-foreground" dir="auto">
        <input
          type="checkbox"
          checked={shared}
          onChange={(e) => setShared(e.target.checked)}
          className="accent-app-primary-500"
        />
        {t('immigration.tracker.docs.sharedAcrossCountries')}
      </label>
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={saving || !name.trim()}
          className="h-9 px-3 rounded-md bg-app-primary-500 text-white text-sm font-medium disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t('common.save')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="h-9 px-3 rounded-md border border-input text-sm text-muted-foreground hover:bg-accent"
        >
          {t('common.cancel')}
        </button>
      </div>
    </form>
  )
}

// ---------- MILESTONES --------------------------------------------------

const MILESTONE_STATUSES = ['planned', 'in_progress', 'completed', 'blocked']

function MilestonesSection({ milestones, countrySlug }) {
  const { t } = useTranslation()
  const [adding, setAdding] = useState(false)
  return (
    <Card>
      <CardContent className="p-3 sm:p-4 space-y-3">
        <SectionHeader
          icon={Flag}
          title={t('immigration.tracker.milestones.title')}
          count={milestones.items.length}
          onAdd={() => setAdding(true)}
        />
        {milestones.loading ? (
          <Skeleton className="h-16 w-full" />
        ) : milestones.items.length === 0 && !adding ? (
          <EmptyHint label={t('immigration.tracker.milestones.empty')} />
        ) : (
          <div className="space-y-1.5">
            {milestones.items.map((m) => (
              <MilestoneRow key={m.id} milestone={m} api={milestones} />
            ))}
          </div>
        )}
        {adding && (
          <AddMilestoneForm
            countrySlug={countrySlug}
            onSubmit={async (values) => {
              await milestones.create(values)
              setAdding(false)
            }}
            onCancel={() => setAdding(false)}
          />
        )}
      </CardContent>
    </Card>
  )
}

function MilestoneRow({ milestone, api }) {
  const { t } = useTranslation()
  const confirm = useConfirm()
  const cycleStatus = async () => {
    const idx = MILESTONE_STATUSES.indexOf(milestone.status)
    const next = MILESTONE_STATUSES[(idx + 1) % MILESTONE_STATUSES.length]
    const patch = { status: next }
    if (next === 'completed' && !milestone.actual_date) {
      patch.actual_date = new Date().toLocaleDateString('en-CA')
    }
    await api.update(milestone.id, patch)
  }
  const onDelete = async () => {
    const ok = await confirm({
      title: t('common.confirmDelete'),
      message: milestone.title,
      destructive: true,
    })
    if (ok) await api.remove(milestone.id)
  }
  return (
    <div className="flex items-center gap-2 px-2.5 py-2 rounded-md border border-input">
      <button
        type="button"
        onClick={cycleStatus}
        className={cn(
          'flex-shrink-0 w-7 h-7 rounded-full inline-flex items-center justify-center',
          milestone.status === 'completed' && 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
          milestone.status === 'in_progress' && 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
          milestone.status === 'planned' && 'bg-muted text-muted-foreground',
          milestone.status === 'blocked' && 'bg-red-500/15 text-red-700 dark:text-red-400',
        )}
        title={t(`immigration.tracker.milestoneStatus.${milestone.status}`)}
      >
        {milestone.status === 'completed' ? <CheckCircle2 className="w-4 h-4" />
          : milestone.status === 'in_progress' ? <CircleDot className="w-4 h-4" />
          : milestone.status === 'blocked' ? <AlertCircle className="w-4 h-4" />
          : <Clock className="w-3.5 h-3.5" />}
      </button>
      <div className="flex-1 min-w-0" dir="auto">
        <p className="text-sm font-medium text-foreground truncate">{milestone.title}</p>
        <p className="text-[10px] text-muted-foreground truncate">
          {milestone.target_date && `${t('immigration.tracker.milestones.target')}: ${milestone.target_date}`}
          {milestone.actual_date && ` · ${t('immigration.tracker.milestones.done')}: ${milestone.actual_date}`}
        </p>
      </div>
      <button
        type="button"
        onClick={onDelete}
        aria-label={t('common.delete')}
        className="p-1.5 rounded-md text-muted-foreground hover:bg-accent hover:text-red-600"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  )
}

function AddMilestoneForm({ countrySlug, onSubmit, onCancel }) {
  const { t } = useTranslation()
  const [title, setTitle] = useState('')
  const [targetDate, setTargetDate] = useState('')
  const [saving, setSaving] = useState(false)
  const submit = async (e) => {
    e.preventDefault()
    if (!title.trim()) return
    setSaving(true)
    await onSubmit({
      title: title.trim(),
      country_slug: countrySlug,
      target_date: targetDate || null,
    })
    setSaving(false)
  }
  return (
    <form onSubmit={submit} className="space-y-2 p-3 rounded-md border border-input bg-muted/30">
      <input
        autoFocus
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder={t('immigration.tracker.milestones.namePlaceholder')}
        className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
      />
      <input
        type="date"
        value={targetDate}
        onChange={(e) => setTargetDate(e.target.value)}
        dir="ltr"
        className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm font-mono"
      />
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={saving || !title.trim()}
          className="h-9 px-3 rounded-md bg-app-primary-500 text-white text-sm font-medium disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t('common.save')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="h-9 px-3 rounded-md border border-input text-sm text-muted-foreground hover:bg-accent"
        >
          {t('common.cancel')}
        </button>
      </div>
    </form>
  )
}

// ---------- COSTS -------------------------------------------------------

const COST_CATEGORIES = ['visa_fee', 'translation', 'exam', 'apostille', 'travel', 'consultancy', 'equipment', 'other']

function CostsSection({ costs, countrySlug }) {
  const { t } = useTranslation()
  const [adding, setAdding] = useState(false)
  const total = costs.items.reduce((s, c) => s + Number(c.amount_sar || 0), 0)
  const paid = costs.items.filter((c) => c.paid).reduce((s, c) => s + Number(c.amount_sar || 0), 0)
  return (
    <Card>
      <CardContent className="p-3 sm:p-4 space-y-3">
        <SectionHeader
          icon={Wallet}
          title={t('immigration.tracker.costs.title')}
          count={costs.items.length}
          onAdd={() => setAdding(true)}
        />
        {costs.items.length > 0 && (
          <div className="flex items-center justify-between text-[11px]" dir="auto">
            <span className="text-muted-foreground">
              {t('immigration.tracker.costs.totals')}:
            </span>
            <span className="font-mono" dir="ltr">
              <span className="text-emerald-600">{fmtMoney(paid)}</span>
              {' / '}
              <span className="text-foreground">{fmtMoney(total)} SAR</span>
            </span>
          </div>
        )}
        {costs.loading ? (
          <Skeleton className="h-16 w-full" />
        ) : costs.items.length === 0 && !adding ? (
          <EmptyHint label={t('immigration.tracker.costs.empty')} />
        ) : (
          <div className="space-y-1.5">
            {costs.items.map((c) => (
              <CostRow key={c.id} cost={c} api={costs} />
            ))}
          </div>
        )}
        {adding && (
          <AddCostForm
            countrySlug={countrySlug}
            onSubmit={async (values) => {
              await costs.create(values)
              setAdding(false)
            }}
            onCancel={() => setAdding(false)}
          />
        )}
      </CardContent>
    </Card>
  )
}

function CostRow({ cost, api }) {
  const { t } = useTranslation()
  const confirm = useConfirm()
  const togglePaid = async () => {
    await api.update(cost.id, {
      paid: !cost.paid,
      incurred_date: !cost.paid ? (cost.incurred_date || new Date().toLocaleDateString('en-CA')) : cost.incurred_date,
    })
  }
  const onDelete = async () => {
    const ok = await confirm({
      title: t('common.confirmDelete'),
      message: cost.name,
      destructive: true,
    })
    if (ok) await api.remove(cost.id)
  }
  return (
    <div className="flex items-center gap-2 px-2.5 py-2 rounded-md border border-input">
      <button
        type="button"
        onClick={togglePaid}
        className={cn(
          'flex-shrink-0 w-7 h-7 rounded-full inline-flex items-center justify-center',
          cost.paid
            ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
            : 'bg-muted text-muted-foreground hover:bg-accent',
        )}
        title={cost.paid ? t('immigration.tracker.costs.markUnpaid') : t('immigration.tracker.costs.markPaid')}
      >
        {cost.paid ? <Check className="w-4 h-4" /> : <XIcon className="w-3.5 h-3.5" />}
      </button>
      <div className="flex-1 min-w-0" dir="auto">
        <p className="text-sm font-medium text-foreground truncate">{cost.name}</p>
        <p className="text-[10px] text-muted-foreground truncate">
          {t(`immigration.tracker.costCategory.${cost.category}`)}
          {cost.incurred_date ? ` · ${cost.incurred_date}` : ''}
        </p>
      </div>
      <span className="text-sm font-mono font-semibold text-foreground flex-shrink-0" dir="ltr">
        {fmtMoney(cost.amount_sar)}
      </span>
      <button
        type="button"
        onClick={onDelete}
        aria-label={t('common.delete')}
        className="p-1.5 rounded-md text-muted-foreground hover:bg-accent hover:text-red-600"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  )
}

function AddCostForm({ countrySlug, onSubmit, onCancel }) {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [amount, setAmount] = useState('')
  const [category, setCategory] = useState('visa_fee')
  const [paid, setPaid] = useState(false)
  const [saving, setSaving] = useState(false)
  const submit = async (e) => {
    e.preventDefault()
    if (!name.trim() || !amount) return
    setSaving(true)
    await onSubmit({
      name: name.trim(),
      amount_sar: Number(amount),
      category,
      paid,
      country_slug: countrySlug,
      incurred_date: paid ? new Date().toLocaleDateString('en-CA') : null,
    })
    setSaving(false)
  }
  return (
    <form onSubmit={submit} className="space-y-2 p-3 rounded-md border border-input bg-muted/30">
      <input
        autoFocus
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={t('immigration.tracker.costs.namePlaceholder')}
        className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
      />
      <input
        type="number"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        placeholder={t('immigration.tracker.costs.amountPlaceholder')}
        dir="ltr"
        className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm font-mono"
      />
      <div className="flex flex-wrap gap-1.5">
        {COST_CATEGORIES.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setCategory(c)}
            className={cn(
              'px-2 py-1 rounded-full text-[11px] border',
              category === c
                ? 'bg-app-primary-500 text-white border-app-primary-500'
                : 'border-input text-muted-foreground hover:bg-accent',
            )}
          >
            {t(`immigration.tracker.costCategory.${c}`)}
          </button>
        ))}
      </div>
      <label className="inline-flex items-center gap-2 text-xs text-muted-foreground" dir="auto">
        <input
          type="checkbox"
          checked={paid}
          onChange={(e) => setPaid(e.target.checked)}
          className="accent-app-primary-500"
        />
        {t('immigration.tracker.costs.alreadyPaid')}
      </label>
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={saving || !name.trim() || !amount}
          className="h-9 px-3 rounded-md bg-app-primary-500 text-white text-sm font-medium disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t('common.save')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="h-9 px-3 rounded-md border border-input text-sm text-muted-foreground hover:bg-accent"
        >
          {t('common.cancel')}
        </button>
      </div>
    </form>
  )
}

// ---------- shared bits --------------------------------------------------

function SectionHeader({ icon: Icon, title, count, onAdd }) {
  const { t } = useTranslation()
  return (
    <div className="flex items-center gap-2">
      <Icon className="w-4 h-4 text-app-primary-500 flex-shrink-0" />
      <h2 className="text-sm font-semibold text-foreground flex-1" dir="auto">
        {title}
        {count > 0 && <span className="ms-1.5 text-muted-foreground font-normal">({count})</span>}
      </h2>
      <button
        type="button"
        onClick={onAdd}
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs text-app-primary-500 hover:bg-app-primary-500/10"
      >
        <Plus className="w-3.5 h-3.5" />
        <span>{t('common.add')}</span>
      </button>
    </div>
  )
}

function EmptyHint({ label }) {
  return (
    <p className="text-xs text-muted-foreground italic py-2" dir="auto">{label}</p>
  )
}
